import { env } from "@/env.mjs";
import {
  getOrCreateMarketingSessionId,
  getOrCreateMarketingVisitorId,
  readMarketingUtm,
} from "./visitor";

export type MarketingTrackFunnel = "careers_developers" | "website_demo";

export interface MarketingTrackArgs {
  readonly funnel: MarketingTrackFunnel;
  readonly step: number;
  readonly converted?: boolean;
  readonly blocked?: boolean;
}

const recentKeys = new Map<string, number>();
const DEDUPE_MS: number = 2000;

function shouldSkipDuplicate(key: string, now: number): boolean {
  const prev: number | undefined = recentKeys.get(key);
  if (prev !== undefined && now - prev < DEDUPE_MS) return true;
  recentKeys.set(key, now);
  if (recentKeys.size > 30) {
    for (const [storedKey, storedAt] of recentKeys) {
      if (now - storedAt > DEDUPE_MS) recentKeys.delete(storedKey);
    }
  }
  return false;
}

/**
 * Fire-and-forget funnel ping. Never throws into page UI.
 */
export function trackMarketingEvent(args: MarketingTrackArgs): void {
  if (typeof window === "undefined") return;
  try {
    const site: string = env.NEXT_PUBLIC_CONVEX_SITE_URL.replace(/\/$/, "");
    const visitorId: string = getOrCreateMarketingVisitorId();
    const sessionId: string = getOrCreateMarketingSessionId();
    const now: number = Date.now();
    const key: string = [
      visitorId,
      sessionId,
      args.funnel,
      String(args.step),
      args.converted === true ? "1" : "0",
      args.blocked === true ? "1" : "0",
    ].join("\0");
    if (shouldSkipDuplicate(key, now)) return;
    const utm: string | undefined = readMarketingUtm();
    const body: Record<string, unknown> = {
      visitor_id: visitorId,
      session_id: sessionId,
      funnel: args.funnel,
      step: args.step,
    };
    if (args.converted === true) body.converted = true;
    if (args.blocked === true) body.blocked = true;
    if (utm !== undefined) body.utm = utm;
    void fetch(`${site}/marketing/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    return;
  }
}
